import React, { useEffect, useState } from 'react'
import { t } from '../i18n'

const DISMISS_KEY = 'corendon_pwa_dismissed_v1'

function isStandalone() {
  if (typeof window === 'undefined') return false
  if (window.matchMedia('(display-mode: standalone)').matches) return true
  if (window.navigator.standalone) return true // iOS Safari
  return false
}

function isMobile() {
  if (typeof window === 'undefined') return false
  return window.matchMedia('(max-width: 768px)').matches
}

function isIos() {
  if (typeof navigator === 'undefined') return false
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream
}

export default function InstallPwaSheet({ locale }) {
  const [visible, setVisible] = useState(false)
  const [deferredPrompt, setDeferredPrompt] = useState(null)

  useEffect(() => {
    if (!isMobile()) return
    if (isStandalone()) return
    try {
      if (localStorage.getItem(DISMISS_KEY) === '1') return
    } catch {
      /* ignore */
    }

    // Android: capture install prompt for later
    const onBeforeInstall = (e) => {
      e.preventDefault()
      setDeferredPrompt(e)
      setVisible(true)
    }
    window.addEventListener('beforeinstallprompt', onBeforeInstall)

    // iOS doesn't fire beforeinstallprompt — show the manual instructions sheet
    // after a short delay so the page can render first.
    let timer = null
    if (isIos()) {
      timer = window.setTimeout(() => setVisible(true), 1500)
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall)
      if (timer) window.clearTimeout(timer)
    }
  }, [])

  const dismiss = () => {
    setVisible(false)
    try {
      localStorage.setItem(DISMISS_KEY, '1')
    } catch {
      /* ignore */
    }
  }

  const accept = async () => {
    if (deferredPrompt) {
      try {
        await deferredPrompt.prompt()
        await deferredPrompt.userChoice
      } catch {
        /* ignore */
      }
      setDeferredPrompt(null)
    }
    dismiss()
  }

  if (!visible) return null

  const ios = isIos()
  return (
    <div className="fixed inset-x-0 bottom-0 z-40 mx-auto max-w-md p-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
      <div className="rounded-3xl border border-gray-200 bg-white p-4 text-sm text-gray-800 shadow-2xl">
        <div className="flex items-start gap-3">
          <div className="text-2xl" aria-hidden>
            📲
          </div>
          <div className="flex-1">
            <div className="font-medium text-gray-900">{t(locale, 'brand')}</div>
            <p className="mt-1 text-gray-600">
              {ios ? t(locale, 'installPwaIos') : t(locale, 'installPwaAndroid')}
            </p>
          </div>
        </div>
        <div className="mt-3 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={dismiss}
            className="rounded-full px-3 py-1.5 text-xs text-gray-500 hover:text-gray-800"
          >
            {t(locale, 'installPwaDismiss')}
          </button>
          {!ios && deferredPrompt && (
            <button
              type="button"
              onClick={accept}
              className="rounded-full bg-corendon-red px-4 py-1.5 text-xs font-medium text-white hover:bg-corendon-red-dark"
            >
              {t(locale, 'installPwaButton')}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
